import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeOff, Globe, KanbanSquare, Lock } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { FEATURES } from '../../../shared/lib/permissions';
import { useDespublicarVaga, useEncerrarVaga, usePublicarVaga, useVagas } from '../hooks';
import type { JobOpening, PublishOpeningPayload, WorkModel } from '../api';
import { VagaStatusBadge } from '../components/RecrutamentoBadges';
import { WORK_MODEL_LABELS, formatClienteLabel, formatDateBR } from '../components/format';
import { PublicarVagaModal } from '../components/PublicarVagaModal';

/**
 * Vagas aprovadas: publicar, editar o anúncio, despublicar e encerrar.
 *
 * Só o anúncio é editável aqui — salário previsto, centro de custo e
 * justificativa continuam na requisição, que é onde a governança mora.
 */
export default function VagasPage(): React.ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [publicando, setPublicando] = useState<JobOpening | null>(null);

  const { data, isLoading, isError } = useVagas({ search: search || undefined, limit: 50 });
  const publicar = usePublicarVaga();
  const despublicar = useDespublicarVaga();
  const encerrar = useEncerrarVaga();

  const vagas = data?.data ?? [];

  return (
    <PageContainer title="Vagas" subtitle="Anúncios das requisições aprovadas">
      <div className="max-w-md">
        <TextInput
          label="Buscar por título ou código da requisição"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {isError && <Alert variant="error">Não foi possível carregar as vagas.</Alert>}

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : vagas.length === 0 ? (
        <EmptyState
          title="Nenhuma vaga"
          description="Vagas aparecem aqui quando uma requisição é aprovada pela cadeia completa."
        />
      ) : (
        <div className="space-y-3">
          {vagas.map((vaga) => (
            <div
              key={vaga.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {vaga.title ?? vaga.requisition?.positionTitle ?? 'Vaga sem título'}
                  </h3>
                  <VagaStatusBadge status={vaga.status} />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {vaga.requisition?.code ?? '—'}
                  {vaga.requisition?.client
                    ? ` · ${formatClienteLabel(vaga.requisition.client)}`
                    : ''}
                  {vaga.city ? ` · ${vaga.city}` : ''}
                  {vaga.recruiter ? ` · Recrutador: ${vaga.recruiter.name}` : ''}
                  {vaga.workModel ? ` · ${WORK_MODEL_LABELS[vaga.workModel]}` : ''}
                  {vaga.deadline ? ` · até ${formatDateBR(vaga.deadline)}` : ''}
                  {` · ${vaga.approvedCount}/${vaga.requisition?.vacanciesCount ?? '?'} preenchidas`}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Can feature={FEATURES.recrutamentoProcessoSeletivo}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<KanbanSquare className="h-4 w-4" />}
                    onClick={() => navigate(`/recrutamento/processo-seletivo/${vaga.id}`)}
                  >
                    Processo seletivo
                  </Button>
                </Can>

                <Can feature={FEATURES.recrutamentoPublicacao} action="edit">
                  {(vaga.status === 'nao_publicada' || vaga.status === 'despublicada') && (
                    <Button
                      size="sm"
                      icon={<Globe className="h-4 w-4" />}
                      onClick={() => setPublicando(vaga)}
                    >
                      Publicar
                    </Button>
                  )}
                  {vaga.status === 'publicada' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<EyeOff className="h-4 w-4" />}
                      loading={despublicar.isPending}
                      onClick={() => despublicar.mutate(vaga.id)}
                    >
                      Despublicar
                    </Button>
                  )}
                  {vaga.status !== 'encerrada' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Lock className="h-4 w-4" />}
                      loading={encerrar.isPending}
                      onClick={() => encerrar.mutate(vaga.id)}
                    >
                      Encerrar
                    </Button>
                  )}
                </Can>
              </div>
            </div>
          ))}
        </div>
      )}

      {publicando && (
        <PublicarVagaModal
          open
          vaga={publicando}
          loading={publicar.isPending}
          error={(publicar.error as Error | null)?.message ?? null}
          onCancel={() => setPublicando(null)}
          onConfirm={(payload: PublishOpeningPayload) =>
            publicar.mutate(
              { id: publicando.id, ...payload },
              { onSuccess: () => setPublicando(null) },
            )
          }
        />
      )}
    </PageContainer>
  );
}
